import { useQuery } from '@tanstack/react-query'
import { t } from '@/i18n'
import { getHealth } from '@/lib/api'
import { CURRENT_STAGE_LABEL } from '@/lib/stage'

/**
 * Порядок робіт за docs/PLAN.md.
 *
 * Керний принцип: усе до етапу 7 включно працює без жодного ключа біржі
 * чи банку. Автоматизація ключами — окремий опційний блок, який прибирає
 * ручну працю, але не додає можливостей.
 */
const ROADMAP = [
  { stage: '0', title: 'sys.s0', detail: 'sys.s0d' },
  { stage: '1', title: 'sys.s1', detail: 'sys.s1d' },
  { stage: '2А', title: 'sys.s2a', detail: 'sys.s2ad' },
  { stage: '2Б', title: 'sys.s2b', detail: 'sys.s2bd' },
  { stage: '3', title: 'sys.s3', detail: 'sys.s3d' },
  { stage: '4', title: 'sys.s4', detail: 'sys.s4d' },
  { stage: '5', title: 'sys.s5', detail: 'sys.s5d' },
  { stage: '6', title: 'sys.s6', detail: 'sys.s6d' },
  { stage: '7', title: 'sys.s7', detail: 'sys.s7d' },
  { stage: '+', title: 'sys.sp', detail: 'sys.spd' },
] as const

const CURRENT_STAGE_INDEX = ROADMAP.findIndex((item) => item.stage === CURRENT_STAGE_LABEL)

export function Status() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['health'],
    queryFn: getHealth,
    refetchInterval: 15_000,
    retry: 1,
  })

  return (
    <div>
      <h2 className="text-sm font-medium text-slate-300">{t('sys.title')}</h2>
      <p className="mt-1 text-sm text-slate-500">{t('sys.lead')}</p>

      <section className="mt-8 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('sys.backend')}</h2>
        {isLoading && <p className="mt-3 text-sm text-slate-500">{t('sys.checking')}</p>}
        {isError && (
          <p className="mt-3 text-sm text-loss">
            {t('sys.noApi')} {error instanceof Error ? error.message : t('sys.unknown')}
          </p>
        )}
        {data && (
          <dl className="mt-3 space-y-1.5 text-sm">
            <Row label={t('sys.status')} value={data.status} ok={data.status === 'ok'} />
            <Row label={t('sys.env')} value={data.environment} ok={undefined} />
            {Object.entries(data.checks).map(([key, value]) => (
              <Row key={key} label={key} value={String(value)} ok={checkState(String(value))} />
            ))}
          </dl>
        )}
      </section>

      <section className="mt-6 rounded-lg border border-surface-border bg-surface-raised p-5">
        <h2 className="text-sm font-medium text-slate-300">{t('sys.plan')}</h2>
        <ol className="mt-3 space-y-2.5">
          {ROADMAP.map((item, index) => {
            const done = index <= CURRENT_STAGE_INDEX
            const active = item.stage === '+'
            return (
              <li key={item.stage} className="flex gap-3 text-sm">
                <span
                  className={[
                    'num mt-0.5 flex h-5 min-w-6 shrink-0 items-center justify-center rounded px-1 text-[11px]',
                    done
                      ? 'bg-profit/15 text-profit'
                      : active
                        ? 'bg-pending/15 text-pending'
                        : 'bg-slate-700/30 text-slate-600',
                  ].join(' ')}
                >
                  {item.stage}
                </span>
                <div>
                  <div className={active ? 'text-slate-100' : 'text-slate-400'}>{t(item.title)}</div>
                  <div className="text-xs text-slate-600">{t(item.detail)}</div>
                </div>
              </li>
            )
          })}
        </ol>
      </section>
    </div>
  )
}

const FAILURE_VALUES = new Set(['error', 'not applied', 'degraded', 'unavailable'])

/**
 * Значення перевірки не завжди «ok» чи «error»: версія міграції — це просто
 * інформація. Забарвлювати її як помилку означає щодня показувати тривогу
 * там, де все гаразд, і привчати не звертати уваги на червоне.
 */
function checkState(value: string): boolean | undefined {
  if (value === 'ok') return true
  if (FAILURE_VALUES.has(value)) return false
  return undefined
}

function Row({
  label,
  value,
  ok,
}: {
  label: string
  value: string
  // `boolean | undefined` явно, а не `ok?: boolean`: у tsconfig увімкнено
  // exactOptionalPropertyTypes, і він розрізняє «властивості немає» та
  // «властивість є і дорівнює undefined». Тут потрібне друге — третій стан
  // «нейтрально», без забарвлення.
  ok: boolean | undefined
}) {
  return (
    <div className="flex justify-between border-b border-surface-border/60 pb-1.5 last:border-0">
      <dt className="text-slate-500">{label}</dt>
      <dd className={['num', ok === undefined ? 'text-slate-300' : ok ? 'text-profit' : 'text-loss'].join(' ')}>
        {value}
      </dd>
    </div>
  )
}
